<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import instance from '@/utils/request';
import { useAuthStore } from '@/stores/auth';
import { enumLabel, statusTagType } from '@/utils/enum-labels';
import { formatDateTime, memberLabel } from '@/utils/format';

const router = useRouter();
const auth = useAuthStore();
const loading = ref(false);

// 健康管理师视角:非超管(无 * 权限)且有患者绑定权限
const isManager = computed(() => !auth.hasPermission('*') && auth.hasPermission('HEALTH_PATIENT_LIST'));

// ───── 平台视角(超管/运营角色) ─────
const stats = ref<any>(null);
// 平台视角下可见预警列表的账号(超管等)也展示未处理预警计数(全部预警,与超管数据范围一致)
const canViewAlerts = computed(() => auth.hasPermission('HEALTH_ALERT_LIST'));
const alertStats = ref<any>(null);

const todoItems = computed(() => {
  const t = stats.value?.todos;
  if (!t) return [];
  return [
    { key: 'shipping', label: '待发货订单', count: t.shipping, path: '/order/list', hint: '已支付待发货' },
    { key: 'refunds', label: '退款审核', count: t.refunds, path: '/order/refund', hint: '待审核退款申请' },
    { key: 'withdrawals', label: '提现审核', count: t.withdrawals, path: '/finance/withdrawal', hint: '待审核提现申请' },
    { key: 'consultations', label: '咨询待回复', count: t.consultations, path: '/content/consult', hint: '会员咨询待回复' },
    { key: 'squareReviews', label: '广场内容审核', count: t.squareReviews, path: '/content/square', hint: '待审核帖子' },
    { key: 'volunteerReviews', label: '志愿者报名审核', count: t.volunteerReviews, path: '/health/volunteer', hint: '待审核报名' },
    { key: 'patientBindings', label: '患者绑定确认', count: t.patientBindings, path: '/health/patient', hint: '待确认绑定申请' },
  ];
});

const pendingCount = computed(() => todoItems.value.reduce((sum, item) => sum + (item.count || 0), 0));

const fetchStats = async () => {
  loading.value = true;
  try {
    stats.value = await instance.get('/admin/dashboard/stats');
    if (canViewAlerts.value) {
      try {
        alertStats.value = await instance.get('/admin/dashboard/manager-stats');
      } catch (err) {
        console.error(err);
      }
    }
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
};

// ───── 健康管理师视角 ─────
const managerStats = ref<any>(null);

const managerTodos = computed(() => {
  const s = managerStats.value;
  if (!s) return [];
  return [
    { key: 'pendingBindings', label: '待确认绑定', count: s.pendingBindings, path: '/health/patient', hint: '患者绑定申请待确认' },
    { key: 'unreadAlerts', label: '未处理预警', count: s.unreadAlerts, path: '/health/alert', hint: '患者异常数据预警' },
    { key: 'pendingConsultations', label: '咨询待回复', count: s.pendingConsultations, path: '/content/consult', hint: '患者咨询待回复' },
  ];
});

const managerPendingCount = computed(() => managerTodos.value.reduce((sum, item) => sum + (item.count || 0), 0));

const managerShortcuts = [
  { title: '患者绑定', desc: '绑定 / 解绑我的患者', path: '/health/patient' },
  { title: '会员健康', desc: '查看患者健康档案', path: '/health/member-health' },
  { title: '健康预警', desc: '处理患者异常预警', path: '/health/alert' },
  { title: '健康记录', desc: '查看患者指标记录', path: '/health/record' },
];

const fetchManagerStats = async () => {
  loading.value = true;
  try {
    managerStats.value = await instance.get('/admin/dashboard/manager-stats');
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const go = (path: string) => router.push(path);
const goMember = (memberId: number) => router.push({ path: '/health/member-health', query: { memberId: String(memberId) } });

onMounted(() => (isManager.value ? fetchManagerStats() : fetchStats()));
</script>

<template>
  <div v-loading="loading" class="page">
    <!-- 健康管理师工作台 -->
    <template v-if="isManager">
      <AdminPageHeader title="工作台" hint="我的患者服务概览" />

      <!-- 待办事项 -->
      <ElCard v-if="managerStats" shadow="never" class="todo-card">
        <template #header>
          <div class="card-head">
            <span>待办事项</span>
            <ElBadge v-if="managerPendingCount > 0" :value="managerPendingCount" type="warning" />
            <span v-else class="all-done">全部处理完毕</span>
          </div>
        </template>
        <div class="todo-grid">
          <div
            v-for="item in managerTodos"
            :key="item.key"
            class="todo-item"
            :class="{ active: item.count > 0 }"
            @click="go(item.path)"
          >
            <div class="todo-count" :class="{ zero: !item.count }">{{ item.count || 0 }}</div>
            <div class="todo-label">{{ item.label }}</div>
            <div class="todo-hint">{{ item.hint }}</div>
          </div>
        </div>
      </ElCard>

      <!-- 我的患者指标 -->
      <div v-if="managerStats" class="stats-grid">
        <div class="stat-card">
          <div class="label">我的患者</div>
          <div class="value">{{ managerStats.boundPatients }}</div>
          <div class="delta">已绑定服务中</div>
        </div>
        <div class="stat-card">
          <div class="label">今日患者记录</div>
          <div class="value">{{ managerStats.todayRecords }}</div>
          <div class="delta">患者今日上报的健康数据</div>
        </div>
        <div class="stat-card">
          <div class="label">未处理预警</div>
          <div class="value" :class="{ 'value-danger': managerStats.unreadAlerts > 0 }">{{ managerStats.unreadAlerts }}</div>
          <div class="delta">{{ managerStats.unreadAlerts > 0 ? '需要尽快跟进' : '一切正常' }}</div>
        </div>
      </div>

      <!-- 最近预警 -->
      <ElCard v-if="managerStats" shadow="never" class="alert-card">
        <template #header>
          <div class="card-head">
            <span>最近预警</span>
            <ElButton link type="primary" size="small" @click="go('/health/alert')">全部预警</ElButton>
          </div>
        </template>
        <ElTable :data="managerStats.recentAlerts || []" size="small">
          <ElTableColumn label="患者" width="160">
            <template #default="{ row }">
              <ElButton link type="primary" @click="goMember(row.memberId)">{{ memberLabel(row.memberNickname, row.memberId) }}</ElButton>
            </template>
          </ElTableColumn>
          <ElTableColumn label="类型" width="140"><template #default="{ row }">{{ enumLabel(row.alertType) }}</template></ElTableColumn>
          <ElTableColumn label="内容" min-width="220"><template #default="{ row }">{{ row.message || '-' }}</template></ElTableColumn>
          <ElTableColumn label="状态" width="90"><template #default="{ row }"><ElTag size="small" :type="statusTagType(row.status)">{{ enumLabel(row.status) }}</ElTag></template></ElTableColumn>
          <ElTableColumn label="时间" width="170"><template #default="{ row }">{{ formatDateTime(row.createdAt) }}</template></ElTableColumn>
          <template #empty>暂无预警,患者数据一切正常</template>
        </ElTable>
      </ElCard>

      <!-- 快捷入口 -->
      <div class="shortcut-grid">
        <div v-for="s in managerShortcuts" :key="s.path" class="shortcut-card" @click="go(s.path)">
          <div class="shortcut-title">{{ s.title }}</div>
          <div class="shortcut-desc">{{ s.desc }}</div>
        </div>
      </div>
    </template>

    <!-- 平台运营工作台(超管/其他角色) -->
    <template v-else>
      <AdminPageHeader title="工作台" hint="平台运营数据与待办事项概览" />

      <!-- 待办事项 -->
      <ElCard v-if="stats" shadow="never" class="todo-card">
        <template #header>
          <div class="card-head">
            <span>待办事项</span>
            <ElBadge v-if="pendingCount > 0" :value="pendingCount" type="warning" />
            <span v-else class="all-done">全部处理完毕</span>
          </div>
        </template>
        <div class="todo-grid">
          <div
            v-for="item in todoItems"
            :key="item.key"
            class="todo-item"
            :class="{ active: item.count > 0 }"
            @click="go(item.path)"
          >
            <div class="todo-count" :class="{ zero: !item.count }">{{ item.count || 0 }}</div>
            <div class="todo-label">{{ item.label }}</div>
            <div class="todo-hint">{{ item.hint }}</div>
          </div>
        </div>
      </ElCard>

      <!-- 核心指标 -->
      <div v-if="stats" class="stats-grid">
        <div class="stat-card">
          <div class="label">会员总数</div>
          <div class="value">{{ stats.memberCount }}</div>
          <div class="delta">今日 +{{ stats.memberTodayNew }}</div>
        </div>
        <div class="stat-card">
          <div class="label">订单总数</div>
          <div class="value">{{ stats.orderCount }}</div>
          <div class="delta">今日 +{{ stats.orderTodayNew }}</div>
        </div>
        <div class="stat-card">
          <div class="label">今日订单金额</div>
          <div class="value">¥{{ (stats.orderAmount / 100).toFixed(2) }}</div>
          <div class="delta">支付 ¥{{ (stats.paymentAmount / 100).toFixed(2) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">今日退款金额</div>
          <div class="value">¥{{ (stats.refundAmount / 100).toFixed(2) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">内容数</div>
          <div class="value">{{ stats.contentCount }}</div>
        </div>
        <div class="stat-card">
          <div class="label">咨询数</div>
          <div class="value">{{ stats.consultationCount }}</div>
        </div>
        <div v-if="canViewAlerts" class="stat-card alert-stat" @click="go('/health/alert')">
          <div class="label">未处理预警</div>
          <div class="value" :class="{ 'value-danger': (alertStats?.unreadAlerts ?? 0) > 0 }">{{ alertStats?.unreadAlerts ?? 0 }}</div>
          <div class="delta">{{ (alertStats?.unreadAlerts ?? 0) > 0 ? '需要尽快跟进' : '一切正常' }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.todo-card { margin-bottom: 16px; }
.card-head { display: flex; align-items: center; gap: 10px; justify-content: space-between; }
.all-done { font-size: 12px; color: var(--gyjk-text-secondary, #718078); }
.todo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}
.todo-item {
  padding: 16px;
  border: 1px solid rgba(221, 234, 229, .8);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color .2s, box-shadow .2s;
  background: #fafcfb;
}
.todo-item:hover { border-color: var(--gyjk-primary); box-shadow: 0 2px 8px rgba(61, 167, 112, .12); }
.todo-count { font-size: 26px; font-weight: 700; color: #e6a23c; }
.todo-count.zero { color: #c0c8c4; }
.todo-label { font-size: 13px; font-weight: 600; color: var(--gyjk-text, #1a2b25); margin-top: 4px; }
.todo-hint { font-size: 12px; color: var(--gyjk-text-secondary, #718078); margin-top: 2px; }

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}
.stat-card {
  padding: 22px; background: #fff; border: 1px solid rgba(221, 234, 229, .8); border-radius: 14px; box-shadow: var(--gyjk-shadow);
}
.stat-card.alert-stat { cursor: pointer; transition: border-color .2s, box-shadow .2s; }
.stat-card.alert-stat:hover { border-color: var(--gyjk-primary); box-shadow: 0 2px 8px rgba(61, 167, 112, .12); }
.label { color: var(--gyjk-text-secondary); font-size: 13px; }
.value { color: var(--gyjk-text); font-size: 30px; font-weight: 750; margin: 10px 0; }
.value-danger { color: var(--el-color-danger); }
.delta { color: var(--gyjk-primary); font-size: 12px; }

.alert-card { margin-top: 16px; }

.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.shortcut-card {
  padding: 18px; background: #fff; border: 1px solid rgba(221, 234, 229, .8); border-radius: 14px;
  cursor: pointer; transition: border-color .2s, box-shadow .2s;
}
.shortcut-card:hover { border-color: var(--gyjk-primary); box-shadow: 0 2px 8px rgba(61, 167, 112, .12); }
.shortcut-title { font-size: 15px; font-weight: 600; color: var(--gyjk-text, #1a2b25); }
.shortcut-desc { font-size: 12px; color: var(--gyjk-text-secondary, #718078); margin-top: 6px; }
</style>
